import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  setApplicationExportToExcel,
  setIdentificationError,
} from '~/store/identification-form/slices.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { Box, Button } from '@mui/material';
import {
  columnMenu,
  shouldShowApplicationReportActionColumn,
  SortField,
} from '~/components/ApplicationReports/constants.ts';
import {
  TApplicationReport,
  TApplicationReportContent,
  TApplicationReportsForm,
  TApplicationReportsFormFilter,
} from '~/components/ApplicationReports/types.ts';
import {
  applicationReport,
  archiveApplicationReport,
  restoreApplicationReport,
  verificationApplicationReport,
} from '~/store/identification-form/thunks/applicationReportThunks.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import { TablePaginationForm } from '~/components/ApplicationReports/components/TablePagination.tsx';
import { styles } from '~/components/ApplicationReports/styles.ts';
import { DataGrid, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { columns } from '~/components/ApplicationReports/components/table/columns.tsx';
import { formatDate, getFilterValue } from '~/components/ApplicationReports/utils/utils.tsx';
import { MultiSortPanel } from '~/components/ApplicationReports/components/MultiSortPanel.tsx';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { selectIdentification } from '~/store/identification-form/selectors';
import { RootState } from '~/store/store.ts';
import { ManagerRole } from '~/constants/clientTypes.ts';

interface IApplicationReportsTableProps {
  setIsResetData: React.Dispatch<React.SetStateAction<boolean>>;
}

const APPLICATION_REPORT_ACTION_THUNKS = {
  archive: archiveApplicationReport,
  restore: restoreApplicationReport,
  verification: verificationApplicationReport,
};

export const ApplicationReportsTable = ({ setIsResetData }: IApplicationReportsTableProps) => {
  const dispatch = useAppDispatch();
  const {
    loading: isLoading,
    applicationReportForm,
    applicationReportTable,
    applicationReportActionPendingId,
  } = useSelector(selectIdentification) || {};
  const roles = useSelector((state: RootState) => state.users.user?.roles as ManagerRole[]);

  const appReportForm = applicationReportForm as TApplicationReport | null;

  const [sortModel, setSortModel] = useState<GridSortModel[number][]>([]);
  const [filters, setFilters] = useState<Record<string, string | null>>({});
  const [filterKey, setFilterKey] = useState(0);

  const buildSort = (model: GridSortModel) => {
    if (!model?.length) return null;
    return JSON.stringify(
      model.map((item) => ({
        field: SortField[item.field as keyof typeof SortField],
        direction: item.sort,
      }))
    );
  };

  const fetchData = async (
    page: number,
    model: GridSortModel,
    activeFilters: Record<string, string | null> = filters
  ) => {
    const form: TApplicationReportsFormFilter = {
      fromDate: formatDate(activeFilters.createdAt)
        ? formatDate(activeFilters.createdAt)
        : (applicationReportTable?.fromDate as string),
      toDate: formatDate(activeFilters.createdAt)
        ? formatDate(activeFilters.createdAt)
        : (applicationReportTable?.toDate as string),
      identificationNumber: activeFilters.identificationNumber ?? null,
      status: activeFilters.status ?? getFilterValue(applicationReportTable?.status) ?? null,
      clientType:
        activeFilters.clientType ?? getFilterValue(applicationReportTable?.clientType) ?? null,
      applicationType:
        activeFilters.applicationType ??
        getFilterValue(applicationReportTable?.applicationType) ??
        null,
      lastUpdatedFrom: formatDate(activeFilters.lastUpdatedAt) ?? null,
      lastUpdatedTo:
        formatDate(activeFilters.lastUpdatedAt) ?? applicationReportTable?.lastUpdatedTo ?? null,
      toboId: activeFilters.toboId ?? getFilterValue(applicationReportTable?.toboId) ?? null,
      initiatorType:
        activeFilters.initiatorType ??
        getFilterValue(applicationReportTable?.initiatorType) ??
        null,
      page,
      size: 50,
      sort: buildSort(model),
    };

    try {
      const res = await dispatch(
        applicationReport({ form: form as unknown as TApplicationReportsForm })
      );
      const payload = res.payload as TApplicationReport;
      await dispatch(setApplicationExportToExcel(form as unknown as TApplicationReportsForm));
      setIsResetData(false);
      if (!payload?.content?.length) {
        dispatch(setIdentificationError({ errorMessage: errorMessages.NO_DATA_FOUND }));
      }
    } catch {
      dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
    }
  };

  const fetchResetData = async () => {
    try {
      const res = await dispatch(
        applicationReport({
          form: applicationReportTable as unknown as TApplicationReportsForm,
        })
      );
      const payload = res.payload as TApplicationReport;
      if (!payload?.content?.length) {
        dispatch(setIdentificationError({ errorMessage: errorMessages.NO_DATA_FOUND }));
      }
    } catch {
      dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
    }
  };

  const handleResetFilters = () => {
    setFilters({});
    setSortModel([]);
    setFilterKey((prev) => prev + 1);
    setIsResetData(true);
    fetchResetData();
  };

  const handleFilterChange = (filterModel: GridFilterModel) => {
    const hasUndefinedValue = filterModel.items.some((item) => item.value === undefined);
    if (hasUndefinedValue) return;

    const updatedFilters = {
      ...filters,
      ...Object.fromEntries(filterModel.items.map((item) => [item.field, item.value || null])),
    };

    const hasChanges = Object.keys({ ...filters, ...updatedFilters }).some(
      (key) => filters[key] !== updatedFilters[key]
    );

    if (!hasChanges) return;

    setFilters(updatedFilters);
    fetchData(0, sortModel, updatedFilters);
  };

  const handleSortModelChange = (model: GridSortModel[number][]) => {
    setSortModel(model);
    fetchData(0, model);
  };

  const handlePageChange = (page: number) => fetchData(page, sortModel);

  const currentPage = appReportForm?.page?.number ?? 0;
  const totalPages = appReportForm?.page?.totalPages ?? 0;
  const content = appReportForm?.content || [];
  const totalElements = appReportForm?.page?.totalElements ?? 0;
  const selectedStatus = filters.status ?? getFilterValue(applicationReportTable?.status);

  const handleApplicationAction = async (
    row: TApplicationReportContent,
    actionType: keyof typeof APPLICATION_REPORT_ACTION_THUNKS
  ) => {
    try {
      await dispatch(
        APPLICATION_REPORT_ACTION_THUNKS[actionType]({
          applicationId: row.applicationId,
        })
      ).unwrap();

      await fetchData(currentPage, sortModel);
    } catch (requestError: unknown) {
      const errorMessage =
        (requestError as { data?: { message?: string }; message?: string })?.data?.message ||
        (requestError as { message?: string })?.message ||
        errorMessages.SERVICE_UNAVAILABLE;

      dispatch(setIdentificationError({ errorMessage }));
    }
  };

  const handleArchive = (row: TApplicationReportContent) =>
    void handleApplicationAction(row, 'archive');
  const handleRestore = (row: TApplicationReportContent) =>
    void handleApplicationAction(row, 'restore');
  const handleVerification = (row: TApplicationReportContent) =>
    void handleApplicationAction(row, 'verification');
  const applicationReportActions = {
    isActionColumnVisible: shouldShowApplicationReportActionColumn({
      status: selectedStatus,
      content,
    }),
    roles,
    pendingActionId: applicationReportActionPendingId,
    onArchive: handleArchive,
    onRestore: handleRestore,
    onVerification: handleVerification,
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage, content]);

  return (
    <Box sx={styles.tableContainer}>
      <Box sx={styles.resetFiltersContainer}>
        <Button variant="outlined" onClick={handleResetFilters} disabled={isLoading}>
          Скинути фільтри
        </Button>
      </Box>
      <MultiSortPanel sortModel={sortModel} onChange={handleSortModelChange} disabled={isLoading} />
      <DataGrid
        key={filterKey}
        rows={content}
        columns={columns(currentPage, applicationReportActions)}
        getRowId={(row) => row.applicationWebId}
        sortingMode="server"
        filterMode="server"
        onFilterModelChange={handleFilterChange}
        hideFooter
        disableRowSelectionOnClick
        loading={isLoading}
        rowHeight={100}
        columnHeaderHeight={60}
        localeText={columnMenu}
        sx={styles.table}
      />
      <TablePaginationForm
        currentPage={currentPage}
        totalElements={totalElements}
        totalPages={totalPages}
        isLoading={isLoading ?? false}
        onPageChange={handlePageChange}
      />
      {isLoading && <FullPageLoader />}
    </Box>
  );
};
